<template>
  <b-container class="RuleCreateUpdate mb-3">
    <h1 class="mb-3">
      <span v-if="ruleId">
        Update Rule
      </span>
      <span v-else>Create new rule</span>
    </h1>
    <div v-if="loading">Loading...</div>
    <template v-else>
      <b-form-group label-cols="2" data-cy="Rule-name">
        <template v-slot:label><strong>Name</strong></template>
        <b-form-input
          data-cy="RuleCreateUpdate-name"
          placeholder="The name of the rule"
          v-model="rule.name"
        />
      </b-form-group>
      <b-form-group label-cols="2" data-cy="Rule-description">
        <template v-slot:label><strong>Description</strong></template>
        <b-form-textarea
          data-cy="RuleCreateUpdate-description"
          placeholder="The description of the rule"
          v-model="rule.description"
        />
      </b-form-group>
      <b-card header="Alert triggers on...">
        <b-row>
          <b-col>
            <b-form-group label-cols="3" data-cy="RuleTrigger-collection">
              <template v-slot:label><strong>Collection</strong></template>
              <b-form-select
                data-cy="RuleCreateUpdate-collection"
                v-model="rule.collection"
                :disabled="!selectedTenant"
              >
                <b-form-select-option
                  v-for="collection in collections"
                  :key="collection"
                  :value="collection"
                  >{{ collection }}</b-form-select-option
                >
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-card class="my-2" body-bg-variant="light">
          <div v-if="!conditions.length" class="text-center">
            <h5>
              Since no condition is set, this alert will trigger whatever
              happens on the selected collection.
            </h5>
          </div>
          <b-row
            v-for="(condition, i) in conditions"
            align-v="center"
            align-h="center"
            no-gutters
            :data-cy="`Condition-${i}`"
            :key="`${condition.attribute}-${i}`"
            ><b-col cols="11" :data-cy="`Condition-${i}`">
              <threshold
                v-if="condition.type === 'threshold'"
                :attributes="currentCollectionAttributes"
                :index="i"
                v-model="conditions[i]"
              />
              <geofencing
                v-if="condition.type === 'geofencing'"
                :index="i"
                v-model="conditions[i]"
              /> </b-col
            ><b-col cols="1" class="text-right"
              ><b-button
                title="Delete condition"
                :data-cy="`Condition-${i}--deleteBtn`"
                @click="deleteCondition(i)"
                ><i class="fas fa-trash"></i></b-button></b-col
          ></b-row>
        </b-card>
        <div class="text-center">
          <b-dropdown
            class="m-2"
            data-cy="RuleCreateOrUpdate-addConditionBtn"
            split-variant="outline-primary"
            text="Select a condition to add..."
            variant="primary"
          >
            <b-dropdown-item
              data-cy="AddConditionBtn-addThreshold"
              @click.prevent="addThreshold"
              >Add Threshold</b-dropdown-item
            >
            <b-dropdown-item :disabled="true"
              >Add Geofencing (not available yet)</b-dropdown-item
            >
          </b-dropdown>
        </div>
      </b-card>
      <div class="RuleCreateOrUpdate-actions text-right my-3 ">
        <b-button
          variant="outline-secondary ml-2"
          :to="{ name: 'alert-rule-list' }"
          >Cancel</b-button
        >
        <b-button data-cy="Submit-btn" variant="primary ml-2" @click="onSubmit"
          >Save</b-button
        >
      </div>
    </template>
  </b-container>
</template>

<script>
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import { mapGetters } from 'vuex';
import Threshold from '@/components/alert-rule-conditions/Threshold';
import Geofencing from '@/components/alert-rule-conditions/Geofencing';
import { extractAttributesFromMapping } from '@/mappingUtils.ts';
import {
  createEmptyThreshold,
  koncordeToConditions,
  conditionsToKoncorde
} from '@/ruleConditionsUtils.ts';
import { ALERT_RULES_COLLECTION } from '@/helpers/alertRules';

const filterPath = 'filters';

export default {
  name: 'RuleCreateUpdate',
  components: {
    Geofencing,
    Threshold
  },
  props: {
    ruleId: {
      type: String
    }
  },
  data() {
    return {
      indexes: [],
      collections: [],
      currentCollectionAttributes: null,
      loading: false,
      rule: this.createEmptyRule(),
      conditions: []
    };
  },
  computed: {
    ...mapGetters('tenants', ['selectedTenant']),
    currentIndex() {
      if (!this.selectedTenant) {
        return null;
      }
      return this.selectedTenant._id;
    }
  },
  methods: {
    createEmptyRule() {
      return {
        name: '',
        description: '',
        filters: {},
        collection: ''
      };
    },
    async fetchRule() {
      if (!this.currentIndex) {
        this.$log.warn('Cannot fetch rule. Current index not defined.');
        return;
      }
      this.loading = true;
      const resp = await this.$kuzzle.document.get(
        this.currentIndex,
        ALERT_RULES_COLLECTION,
        this.ruleId
      );
      const emptyRule = this.createEmptyRule();
      this.rule = defaultsDeep(resp._source, emptyRule);
      if (get(this.rule, filterPath, false)) {
        this.conditions = koncordeToConditions(
          get(this.rule, filterPath, null)
        );
      }
      this.loading = false;
    },
    async fetchCollectionsInIndex(index) {
      const resp = await this.$kuzzle.collection.list(index);
      this.collections = resp.collections
        .filter(c => c.type === 'stored')
        .map(c => c.name);
    },
    async fetchCollectionAttributes(index, collection) {
      const mapping = await this.$kuzzle.collection.getMapping(
        index,
        collection,
        { includeKuzzleMeta: true }
      );
      const attributes = extractAttributesFromMapping(mapping.properties);
      this.currentCollectionAttributes = attributes;
    },
    onCancel() {
      this.$router.push({ to: 'RuleList', params: { engine } });
    },
    async onSubmit() {
      if (this.ruleId) {
        return await this.update();
      }
      await this.create();
    },
    async update() {
      this.loading = true;
      try {
        this.rule.filters = conditionsToKoncorde(this.conditions);
        await this.$kuzzle.document.replace(
          this.currentIndex,
          ALERT_RULES_COLLECTION,
          this.ruleId,
          this.rule,
          {
            refresh: 'wait_for'
          }
        );
        this.$log.debug('success');
        this.loading = false;
        this.$router.push({
          name: 'alert-rule-list'
        });
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while updating the rule',
          variant: 'warning',
          toaster: 'b-toaster-bottom-right',
          appendToast: true,
          dismissible: true,
          noAutoHide: true
        });
      }
    },
    async loadAllTheThings() {
      if (!this.currentIndex) {
        this.$log.warn(
          'Cannot load all the things. Current index not defined.'
        );
        return;
      }
      if (this.ruleId) {
        await this.fetchRule();
      }
      await this.fetchCollectionsInIndex(this.currentIndex);
    },
    async create() {
      this.loading = true;
      try {
        this.rule.filters = conditionsToKoncorde(this.conditions);
        await this.$kuzzle.document.create(
          this.currentIndex,
          ALERT_RULES_COLLECTION,
          this.rule,
          null,
          {
            refresh: 'wait_for'
          }
        );
        this.$log.debug('success');
        this.loading = false;
        this.$router.push({
          name: 'alert-rule-list'
        });
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(error.message, {
          title: 'Something went wrong while updating the rule',
          variant: 'warning',
          toaster: 'b-toaster-bottom-right',
          appendToast: true,
          dismissible: true,
          noAutoHide: true
        });
      }
    },
    addThreshold() {
      this.conditions.push(createEmptyThreshold());
    },
    deleteCondition(i) {
      this.conditions.splice(i);
    }
  },
  async mounted() {
    await this.loadAllTheThings();
  },
  watch: {
    async currentIndex(value) {
      if (!value) {
        return;
      }
      await this.loadAllTheThings();
    },
    'rule.collection': {
      async handler(value) {
        if (!value || !this.currentIndex) {
          return;
        }
        await this.fetchCollectionAttributes(this.currentIndex, value);
      }
    }
  }
};
</script>
